import { useCallback, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import useProductCatalogFiltering, { FAVORITES_SEARCH_PARAM, PRODUCT_SEARCH_PARAM } from './useProductCatalogFiltering';

interface ProductSearchUpdateOptions {
  replace?: boolean
  resetToAllProducts?: boolean
}

interface AttributeFilterUpdateOptions {
  clearProductSearch?: boolean
}

export default function useProductCatalogActions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tR] = useTranslation('routes');
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const viewProductAttributeTypes = useAppSelector(state => state.get('appData').get('viewProductAttributeTypes'));
  const {
    activeFiltersByType,
    favoritesSelected,
  } = useProductCatalogFiltering();

  const getAttributeFilterSlug = useCallback((typeId: number) => {
    return viewProductAttributeTypes.get(String(typeId))?.get('slug');
  }, [viewProductAttributeTypes])

  const getSearchParamsWithoutProductSearch = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(PRODUCT_SEARCH_PARAM);

    return nextSearchParams;
  }, [searchParams])

  const updateAttributeFilter = useCallback((typeId: number, filters: Set<string>, options?: AttributeFilterUpdateOptions) => {
    const filterSlug = getAttributeFilterSlug(typeId);
    if (filterSlug === undefined) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    if (options?.clearProductSearch === true) {
      nextSearchParams.delete(PRODUCT_SEARCH_PARAM);
    }

    if (filters.size === 0) {
      nextSearchParams.delete(filterSlug);
    } else {
      nextSearchParams.set(filterSlug, Array.from(filters).join(','));
    }

    startTransition(() => setSearchParams(nextSearchParams));
  }, [getAttributeFilterSlug, searchParams, setSearchParams, startTransition])

  const addAttributeFilter = useCallback((typeId: number, valueId: number, options?: AttributeFilterUpdateOptions) => {
    const filters = new Set(activeFiltersByType.get(typeId) ?? []);
    filters.add(String(valueId));

    updateAttributeFilter(typeId, filters, options);
  }, [activeFiltersByType, updateAttributeFilter])

  const removeAttributeFilter = useCallback((typeId: number, valueId: string) => {
    const filters = new Set(activeFiltersByType.get(typeId) ?? []);
    filters.delete(valueId);

    updateAttributeFilter(typeId, filters);
  }, [activeFiltersByType, updateAttributeFilter])

  const toggleAttributeFilter = useCallback((typeId: number, valueId: number) => {
    const valueIdString = String(valueId);
    const filters = new Set(activeFiltersByType.get(typeId) ?? []);

    if (filters.has(valueIdString)) {
      filters.delete(valueIdString);
    } else {
      filters.add(valueIdString);
    }

    updateAttributeFilter(typeId, filters);
  }, [activeFiltersByType, updateAttributeFilter])

  const updateProductSearch = useCallback((value: string, options?: ProductSearchUpdateOptions) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    const nextValue = value.trim();
    const { resetToAllProducts, ...navigationOptions } = options ?? {};

    if (nextValue === '') {
      nextSearchParams.delete(PRODUCT_SEARCH_PARAM);
    } else {
      nextSearchParams.set(PRODUCT_SEARCH_PARAM, nextValue);
    }

    startTransition(() => {
      if (resetToAllProducts === true && nextValue !== '') {
        const nextSearch = nextSearchParams.toString();
        void navigate(tR('/products') + (nextSearch === '' ? '' : '?' + nextSearch), navigationOptions);
        return;
      }

      setSearchParams(nextSearchParams, navigationOptions);
    });
  }, [navigate, searchParams, setSearchParams, startTransition, tR])

  const clearProductSearch = useCallback(() => {
    updateProductSearch('');
  }, [updateProductSearch])

  const toggleFavorites = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    if (favoritesSelected) {
      nextSearchParams.delete(FAVORITES_SEARCH_PARAM);
    } else {
      nextSearchParams.set(FAVORITES_SEARCH_PARAM, '1');
    }

    startTransition(() => setSearchParams(nextSearchParams));
  }, [favoritesSelected, searchParams, setSearchParams, startTransition])

  const clearFavorites = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(FAVORITES_SEARCH_PARAM);

    startTransition(() => setSearchParams(nextSearchParams));
  }, [searchParams, setSearchParams, startTransition])

  const clearFilters = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    viewProductAttributeTypes.forEach(type => {
      nextSearchParams.delete(type.get('slug'));
    });
    nextSearchParams.delete(FAVORITES_SEARCH_PARAM);
    nextSearchParams.delete(PRODUCT_SEARCH_PARAM);

    startTransition(() => setSearchParams(nextSearchParams));
  }, [searchParams, setSearchParams, startTransition, viewProductAttributeTypes])

  return {
    addAttributeFilter,
    clearFavorites,
    clearFilters,
    clearProductSearch,
    getSearchParamsWithoutProductSearch,
    isPending,
    removeAttributeFilter,
    toggleAttributeFilter,
    toggleFavorites,
    updateProductSearch,
  };
}
